import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { faArrowUpFromArc } from '@fortawesome/pro-light-svg-icons/faArrowUpFromArc';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { LockedControl } from '../../../music24/components/LockedControl/LockedControl';
import { useUpgradePrompt } from '../../../music24/hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../../music24/config/music24PlanCapabilities';
import { usePublishedPagesUsage } from '../../usePublishedPagesUsage';
import { editorMessages } from '../editorMessages';
import { useEditorSaves, useEditorSavesState } from '../saves/useEditorSaves';
import { canPublish, EditorStatus } from './editorStatus';
import { EditorPublish } from './useEditorPublish';

interface SMLEditorPublishButtonProps {
  accountId: number;
  status: EditorStatus;
  blockCount: number;
  publish: EditorPublish;
  stretch?: boolean;
}

/**
 * "Publish" on a page that was never published, "Publish changes" on a live one (design a1–a4). It
 * is off when the page is up to date or has no block. While a change waits for its autosave or a
 * save runs, it is off and says "Saving…". A delete in its Undo window counts as a change: a click
 * sends it before the publish. On Free at the page limit, a first publish is the locked control
 * that opens the plan prompt (design a4, 4).
 */

const SMLEditorPublishButtonComponent: React.FC<SMLEditorPublishButtonProps> = ({
  accountId,
  status,
  blockCount,
  publish,
  stretch,
}) => {
  const intl = useIntl();
  const openUpgradePrompt = useUpgradePrompt();
  const openPagesPrompt = useCallback(() => openUpgradePrompt(SMLUpgradeFeature.pages), [openUpgradePrompt]);
  const { limit, used } = usePublishedPagesUsage(accountId, { skip: status.kind !== 'draft' });
  const isFirstPublish = status.kind === 'draft';
  const label = intl.formatMessage(isFirstPublish ? editorMessages.publish : editorMessages.publishChanges);
  const isPublishing = publish.state.kind === 'publishing';
  const saves = useEditorSavesState(useEditorSaves());
  const isSaving = saves.isBusy && !isPublishing;
  // A block with a wrong field is not saved: a publish would publish its old value (brk-16).
  const hasInvalidBlock = saves.invalidCount > 0 && !isPublishing;
  const hasChange = canPublish(status, blockCount) || (saves.hasUndoWaiting && blockCount > 0);
  let text = label;
  if (isPublishing) {
    text = intl.formatMessage(editorMessages.publishing);
  } else if (isSaving) {
    text = intl.formatMessage(editorMessages.saving);
  } else if (hasInvalidBlock) {
    text = intl.formatMessage(editorMessages.fixBlocksFirst, { count: saves.invalidCount });
  }

  let publishTooltip: string | undefined;
  if (blockCount === 0) {
    publishTooltip = intl.formatMessage(editorMessages.publishNoBlocks);
  } else if (hasInvalidBlock) {
    publishTooltip = intl.formatMessage(editorMessages.fixBlocksHelp);
  }

  if (isFirstPublish && limit !== null && used !== undefined && used >= limit) {
    return (
      <LockedControl
        variant="button"
        feature={SMLUpgradeFeature.pages}
        onClick={openPagesPrompt}
        label={label}
        omitCap
      />
    );
  }

  return (
    <SMLButton
      leftIcon={faArrowUpFromArc}
      color="primary"
      onClick={publish.publish}
      isLoading={isPublishing}
      disabled={!hasChange || isPublishing || isSaving || hasInvalidBlock}
      hoverTooltip={publishTooltip}
      stretch={stretch}
      omitCap
      testID="EditorPublish"
    >
      {text}
    </SMLButton>
  );
};

export const SMLEditorPublishButton = TypedMemo(SMLEditorPublishButtonComponent);
